import React from "react";
import { Link } from "gatsby";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Medication on time",
		"Prompts or safe administration at the prescribed times, with responses and changes recorded for the care team.",
	],
	[
		"02",
		"Movement and mobility",
		"Patient support with walking, transfers and daily movement, following guidance from relevant clinicians and therapists.",
	],
	[
		"03",
		"Personal care",
		"Unhurried help with washing, dressing, continence and grooming, adapted around easier and more difficult periods.",
	],
	[
		"04",
		"Meals and hydration",
		"Regular drinks, balanced meals and support with eating, with swallowing concerns escalated to the appropriate professional.",
	],
	[
		"05",
		"Activity and independence",
		"Encouragement to keep doing meaningful tasks and agreed exercises without taking over what someone can manage themselves.",
	],
	[
		"06",
		"Observation and reassurance",
		"Changes in mobility, sleep, mood, appetite or 'on/off' patterns can be noticed and shared with the wider care team.",
	],
];

const signs = [
	"Tremor or involuntary shaking",
	"Slower movement",
	"Stiff or inflexible muscles",
	"Balance and walking changes",
	"Sleep, mood or anxiety changes",
	"Speech, swallowing or memory changes",
];

const experiences = [
	[
		"Movement symptoms",
		"Tremor, slowness and stiffness are the three main movement symptoms. Balance, posture, walking and facial expression can also be affected.",
	],
	[
		"Non-movement symptoms",
		"Parkinson's can also affect sleep, mood, pain, blood pressure, bladder and bowel function, memory, speech and swallowing. Every person has a different combination.",
	],
	[
		"'On' and 'off' periods",
		"Some people experience times when medication controls movement symptoms well and times when it wears off. Recording patterns can help clinicians review treatment.",
	],
	[
		"Other forms of parkinsonism",
		"Conditions including vascular parkinsonism, drug-induced parkinsonism and multiple system atrophy can share features with Parkinson's but may need different clinical management.",
	],
];

const homePrinciples = [
	[
		"Protect medication timings",
		"Build the day around the prescribed schedule rather than fitting medication around other tasks.",
	],
	[
		"Create clear routes",
		"Reduce trip hazards, improve lighting and keep commonly used spaces easy to move through.",
	],
	[
		"Plan around energy",
		"Allow more time for tasks and arrange demanding activities for the person's better periods where possible.",
	],
	[
		"Follow clinical guidance",
		"Use recommendations from physiotherapy, occupational therapy and speech and language therapy in everyday care.",
	],
];

const faqs = [
	[
		"Can someone with Parkinson's remain at home?",
		"Many people can continue living at home with appropriate support. Whether live-in care is suitable depends on the person's symptoms, mobility, risks, medication, home environment and preferences. Edyn begins with a consultation and assessment.",
	],
	[
		"Can a carer manage time-critical medication?",
		"Where medication support is included in the assessed care plan, a trained carer can prompt or administer it according to the prescription and Edyn's medication procedures. Clinical decisions remain with the person's prescribing healthcare professionals.",
	],
	[
		"How can care respond to 'on' and 'off' periods?",
		"The carer can learn the person's patterns, allow more time when movement is difficult and record relevant observations. Any concerns or significant changes are shared with the Care Manager and appropriate healthcare professional.",
	],
	[
		"Can a live-in carer help with exercise?",
		"A carer can encourage safe activity and support exercises that have been recommended by a physiotherapist or other qualified professional. They do not design a clinical exercise programme themselves.",
	],
	[
		"What happens as Parkinson's progresses?",
		"The Care Manager reviews support as needs change and can update the care plan, reassess risks and coordinate with the family and relevant professionals. If a different level of care is needed, Edyn will discuss that clearly.",
	],
];

export default function ParkinsonsCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Parkinson's Live-in Care at Home | Edyn Care"
				description="Personalised Parkinson's live-in care supporting medication routines, mobility, independence and everyday life at home, managed by Edyn's care team."
				path="/condition/parkinsons/"
				image="https://www.edyn.care/assets/marketing-site/images/condition_parkinsons_splash.jpeg"
				service={{ name: "Parkinson's live-in care", serviceType: "Parkinson's care at home" }}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Parkinson's care", path: "/condition/parkinsons/" },
				]}
			/>
			<Header />

			<main
				id="main-content"
				className="edyn-dementia edyn-dementia--textured edyn-parkinsons"
			>
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">
									Parkinson's care at home
								</p>
								<h1>Support that moves at your pace.</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One-to-one care can support medication routines, movement and
									everyday independence while helping someone remain in the home
									they know.
								</p>
								<ul>
									<li>Care adapted to changing symptoms</li>
									<li>A professional carer in the home</li>
									<li>Ongoing oversight from a Care Manager</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a
										className="button button--text-light"
										href="#parkinsons-support"
									>
										Explore support <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual">
							<img
								src={`${imagePath}/condition_parkinsons_splash.jpeg`}
								alt="An older person holding a warm drink at home"
								width="1920"
								height="1280"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Consistency where it matters.</strong>
									Flexibility where life changes.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Edyn care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One-to-one</strong>
							<span>support in the home</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by an Edyn care team</span>
						</p>
						<p>
							<strong>CQC registered</strong>
							<span>care across England</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">Life beyond the diagnosis</p>
						<div>
							<h2>
								Parkinson's affects movement. Care should protect momentum.
							</h2>
							<div className="dementia-person__copy">
								<p>
									Symptoms can vary from day to day and even across a single
									day. Good support notices those changes, allows enough time
									and does not assume that needing help means losing choice.
								</p>
								<p>
									The care plan begins with the person: how Parkinson's affects
									them, when medication is due, what they want to keep doing and
									which routines make life feel like their own.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="parkinsons-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Reliable help, responsive to each part of the day.</h2>
							</div>
							<p>
								Support follows the agreed care plan and works alongside the
								person's Parkinson's team, GP and therapists.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">Understanding Parkinson's</p>
							<h2>Movement is only part of the picture.</h2>
							<p>
								Parkinson's is a progressive neurological condition caused by
								the loss of nerve cells that produce dopamine. It affects
								everyone differently.
							</p>
							<a
								className="dementia-source"
								href="https://www.nhs.uk/conditions/parkinsons-disease/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read NHS Parkinson's guidance{" "}
								<span aria-hidden="true">&#8599;</span>
							</a>
						</div>
						<ul className="dementia-understanding__signs">
							{signs.map((sign, index) => (
								<li key={sign}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{sign}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/we-care-image-1.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The wider picture</p>
								<h2>Parkinson's facts and figures</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>Around 166,000</strong>
								<span>people are diagnosed with Parkinson's in the UK</span>
							</p>
							<p>
								<strong>Around 28,000</strong>
								<span>people were expected to be diagnosed during 2025</span>
							</p>
							<p>
								<strong>Every 20 minutes</strong>
								<span>someone in the UK receives a Parkinson's diagnosis</span>
							</p>
							<p>
								<strong>59%</strong>
								<span>of people living with Parkinson's in the UK are men</span>
							</p>
							<p>
								<strong>1 in 16</strong>
								<span>were diagnosed before the age of 50</span>
							</p>
							<p>
								<strong>173,000</strong>
								<span>
									people are projected to be living with Parkinson's by 2030
								</span>
							</p>
							<div className="dementia-facts__sources">
								Source: Parkinson's UK. Figures reviewed July 2026.
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">A changing condition</p>
							<h2>Symptoms and support are personal.</h2>
							<p>
								Parkinson's is not experienced in one standard way. Clinical
								guidance and the care assessment help shape support around what
								is happening now.
							</p>
						</div>
						<div className="dementia-types__list">
							{experiences.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual parkinsons-home__visual">
							<img
								src={`${imagePath}/livein-imageclus-05.jpg`}
								alt="An older person resting a hand on a chair at home"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">A supportive home</p>
							<h2>Make movement safer without making home clinical.</h2>
							<p>
								Before care begins, we assess risks and agree practical ways to
								support mobility, routines and independence.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed Parkinson's care
							</p>
							<h2>One carer in the home. A whole care team behind them.</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Understand</h3>
								<p>
									A Care Manager learns about symptoms, medication, mobility,
									risks and everyday priorities.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match</h3>
								<p>
									We select a professional carer with the right experience,
									approach and personal fit.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Manage</h3>
								<p>
									The wider Edyn team oversees quality, continuity and changes
									to the care plan.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/approach/">
							See how Edyn works
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions families ask</p>
							<h2>Clear answers, without pressure.</h2>
							<p>
								Every family starts in a different place. These are some of the
								questions we often hear first.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
